import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { processSquarePayment, formatPrice } from '../services/bookingService';
import SquarePaymentPortal from './SquarePaymentPortal';

const PortalSquarePaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  squareConfig, // { applicationId, locationId }
}) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const [isCardReady, setIsCardReady] = useState(false);
  const portalContainerRef = useRef(null);

  console.log('Portal Square Config received:', squareConfig);

  // Handle card ready from portal
  const handleCardReady = (cardInstance) => {
    console.log('Card ready in portal form');
    setCard(cardInstance);
    setIsCardReady(true);
  };

  // Handle portal errors
  const handlePortalError = (errorMessage) => {
    console.error('Portal error:', errorMessage);
    setError(errorMessage);
  };

  // Handle payment submission
  const handlePayment = async () => {
    if (!card) {
      setError('Payment form not ready. Please try again.');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      console.log('Tokenizing card in portal...');
      const tokenResult = await card.tokenize();
      
      if (tokenResult.status === 'OK') {
        console.log('Payment token received:', tokenResult.token);
        
        // Extract the clinic stub from the URL
        const pathParts = window.location.pathname.split('/');
        const clinicStub = pathParts[1];

        // Prepare payment data with the token
        const paymentData = {
          token: tokenResult.token,
          amount: amount,
          currency: 'AUD',
          clinic_id: clinicId,
          service_id: serviceId,
          practitioner_id: practitionerId,
          patient_email: patientEmail,
          patient_name: patientName,
          appointment_details: appointmentDetails,
          clinic_stub: clinicStub,
          return_url: returnUrl
        };

        // Process payment through backend
        const result = await processSquarePayment(paymentData);
        
        if (result.success) {
          console.log('Payment successful:', result);
          onSuccess(result);
        } else {
          throw new Error(result.error || 'Payment failed');
        }
      } else {
        console.error('Tokenization failed:', tokenResult.errors);
        throw new Error('Failed to process card details. Please check your information.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onCancel}
          sx={{ mr: 2 }}
          disabled={processing}
        >
          Back
        </Button>
        <Typography variant="h6">
          Payment - Portal Square SDK
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enter Payment Details
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Amount: {formatPrice(amount)} | Portal Square Web Payments SDK
        </Typography>

        {!squareConfig?.applicationId || !squareConfig?.locationId ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Square payment configuration is incomplete. 
              {!squareConfig?.applicationId && ' Missing Application ID.'}
              {!squareConfig?.locationId && ' Missing Location ID.'}
            </Alert>
            <Button variant="outlined" onClick={onCancel}>
              Go Back
            </Button>
          </Box>
        ) : processing ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Processing payment...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Card Details
              </Typography>
              
              {/* Portal Container */}
              <div 
                ref={portalContainerRef}
                style={{
                  minHeight: '120px',
                  width: '100%',
                  border: '1px solid #E0E6ED',
                  borderRadius: '6px',
                  padding: '16px',
                  backgroundColor: 'white',
                  position: 'relative'
                }}
              >
                {/* Square Portal Component */}
                <SquarePaymentPortal
                  squareConfig={squareConfig}
                  onCardReady={handleCardReady}
                  onError={handlePortalError}
                  containerId="square-portal-container"
                />
              </div>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button 
                variant="outlined"
                onClick={onCancel}
                disabled={processing}
              >
                Cancel
              </Button>
              
              <Button 
                variant="contained"
                size="large"
                disabled={processing || !isCardReady}
                onClick={handlePayment}
                sx={{ minWidth: 120 }}
              >
                Pay {formatPrice(amount)}
                {processing && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PortalSquarePaymentForm;